import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import {
  BookOpen,
  Briefcase,
  Code,
  Cpu,
  LayoutDashboard,
  LogOut,
  Home,
  Mail,
  Moon,
  Newspaper,
  Sun,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { clearAuthToken, isAuthenticated } from "../utils/adminApi";
import { fetchVisitorSummary } from "../utils/visitorTracking";

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isArticleRoute = location.pathname.startsWith("/articles/");
  const isNtdRoute = location.pathname === "/projects/ntd-intelligence";
  // Default to light mode (false) unless user preference is stored
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });
  const [visitorSummary, setVisitorSummary] = useState(null);
  const [visitorCounterLoading, setVisitorCounterLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(isAuthenticated());

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const syncAuthState = () => setAuthenticated(isAuthenticated());

    syncAuthState();
    window.addEventListener("auth-changed", syncAuthState);

    return () => {
      window.removeEventListener("auth-changed", syncAuthState);
    };
  }, [location.pathname]);

  useEffect(() => {
    let isActive = true;

    const loadVisitorSummary = async () => {
      try {
        const summary = await fetchVisitorSummary();

        if (isActive) {
          setVisitorSummary(summary);
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.error("Visitor summary endpoint is not available yet.");
        } else {
          console.error("Failed to load visitor summary:", error);
        }
      } finally {
        if (isActive) {
          setVisitorCounterLoading(false);
        }
      }
    };

    const handleVisitorSummaryUpdate = (event) => {
      if (event.detail) {
        setVisitorSummary(event.detail);
        setVisitorCounterLoading(false);
      }
    };

    loadVisitorSummary();
    window.addEventListener(
      "visitor-summary-updated",
      handleVisitorSummaryUpdate
    );

    return () => {
      isActive = false;
      window.removeEventListener(
        "visitor-summary-updated",
        handleVisitorSummaryUpdate
      );
    };
  }, []);

  const footerCounterText = visitorCounterLoading
    ? "Loading visitor counter..."
    : visitorSummary
    ? `Unique visitors: ${visitorSummary.totalUniqueVisitors.toLocaleString()} | Countries: ${visitorSummary.totalCountries.toLocaleString()}`
    : "Visitor counter unavailable";

  const handleLogout = () => {
    clearAuthToken();
    navigate("/login");
  };

  return (
    <div
      className={`d-flex flex-column min-vh-100 ${
        darkMode ? "dark-theme" : "light-theme"
      }`}
    >
      {/* Navbar */}
      <nav
        className={`navbar navbar-expand-lg ${
          darkMode ? "bg-dark navbar-dark" : "bg-light navbar-light shadow-sm"
        }`}
      >
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center" to="/">
            <Cpu size={28} className={darkMode ? "text-cyan" : "text-primary"} />
            <span className="ms-2 fw-bold" style={{ fontSize: "1.2rem" }}>
              MG
            </span>
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <Link className="nav-link d-flex align-items-center" to="/">
                  <Home size={18} className="me-1" /> Home
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/aboutme"
                >
                  <BookOpen size={18} className="me-1" /> About
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/experiences"
                >
                  <Briefcase size={18} className="me-1" /> Experiences
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/projects"
                >
                  <Code size={18} className="me-1" /> Projects
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/articlelist"
                >
                  <Code size={18} className="me-1" /> Articles
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/technews"
                >
                  <Newspaper size={18} className="me-1" /> Tech News
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/oldalexhub"
                >
                  <Cpu size={18} className="me-1" /> Old Alex Hub
                </Link>
              </li>
              <li className="nav-item">
                <Link
                  className="nav-link d-flex align-items-center"
                  to="/contactus"
                >
                  <Mail size={18} className="me-1" /> Contact
                </Link>
              </li>
              {authenticated ? (
                <li className="nav-item">
                  <Link
                    className="nav-link d-flex align-items-center"
                    to="/dashboard"
                  >
                    <LayoutDashboard size={18} className="me-1" /> Dashboard
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>
          <div className="d-flex align-items-center gap-2 ms-3">
            {authenticated ? (
              <button
                className={`btn ${
                  darkMode ? "btn-outline-light" : "btn-outline-dark"
                } admin-action-btn`}
                onClick={handleLogout}
              >
                <LogOut size={18} className="me-1" /> Logout
              </button>
            ) : null}
            <button
              className={`btn ${
                darkMode ? "btn-outline-light" : "btn-outline-dark"
              } toggle-darkmode`}
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main
        className={`layout-main flex-grow-1 py-4 ${
          isArticleRoute || isNtdRoute
            ? "layout-main--article container-fluid px-3 px-md-4 px-xl-5"
            : "container"
        }`}
      >
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        className={`text-center py-3 mt-auto ${
          darkMode ? "footer-dark" : "footer-light"
        }`}
      >
        <p className="mb-0">
          &copy; {new Date().getFullYear()} Mohamed Gad | Mobility & Transportation Analytics Leader | BI, Data Governance, Compliance, Service Performance
        </p>
        <nav className="footer-policy-links" aria-label="Site policies">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <span aria-hidden="true">|</span>
          <Link to="/terms-of-use">Terms of Use</Link>
          <span aria-hidden="true">|</span>
          <Link to="/editorial-policy">Editorial Policy</Link>
          <span aria-hidden="true">|</span>
          <Link to="/contactus">Contact</Link>
        </nav>
        <p className="mb-0 mt-2 footer-counter-text">{footerCounterText}</p>
      </footer>

      {/* Embedded Styles */}
      <style>
        {`
          html, body {
            height: 100%;
            margin: 0;
          }

          .dark-theme {
            background: #121212;
            color: #ddd;
          }

          .light-theme {
            background: #f8f9fa;
            color: #333;
          }

          .navbar-dark {
            background: #1a1a1a !important;
            border-bottom: 1px solid #333;
          }

          .navbar-dark .nav-link {
            color: #ddd !important;
            transition: color 0.3s ease-in-out;
          }

          .navbar-dark .nav-link:hover {
            color: #00E0FF !important;
          }

          .navbar-light .nav-link {
            color: #333 !important;
          }

          .navbar-light .nav-link:hover {
            color: #007bff !important;
          }

          .footer-dark {
            background: #1a1a1a;
            color: #00FF99;
          }

          .footer-light {
            background: #f8f9fa;
            color: #333;
          }

          .footer-counter-text {
            font-size: 0.92rem;
            letter-spacing: 0.01em;
            opacity: 0.9;
          }

          .footer-policy-links {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 8px;
            margin-top: 8px;
            font-size: 0.92rem;
          }

          .footer-policy-links a {
            color: inherit;
            font-weight: 600;
            text-decoration: none;
          }

          .footer-policy-links a:hover {
            text-decoration: underline;
          }

          .toggle-darkmode {
            background: transparent;
            border: 1px solid #00E0FF;
          }

          .admin-action-btn {
            white-space: nowrap;
          }

          .toggle-darkmode:hover {
            background: #00E0FF;
            color: #000;
          }

          .layout-main--article {
            width: 100%;
            max-width: 1600px;
            margin: 0 auto;
          }

          .text-cyan {
            color: #00E0FF;
          }

          .text-primary {
            color: #007bff;
          }
        `}
      </style>
    </div>
  );
};

export default Layout;
